import { BlackOverlay } from '@euler/app/components/BlackOverlay';
import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { IconProps } from '@euler/app/components/icons/types';
import { useSvgProps } from '@euler/app/components/icons/util';
import {
  ListMask,
  useScrollableMask,
} from '@euler/app/components/list/ListMask';
import { Typography } from '@euler/app/components/typography';
import { ConstructionJobManager } from '@euler/app/flows/task/functions/task-detail/construction/ConstructionJobManager';
import { useProductManifestItemsForSubject } from '@euler/app/flows/task/functions/useProductManifestItemsForSubject';
import { useTaskNavigation } from '@euler/app/flows/task/functions/useTaskNavigation';
import { BlurView } from '@euler/components/BlurView';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { ModalWrapper } from '@euler/components/ModalWrapper';
import { AppHoldItem, AppMenu } from '@euler/components/holdmenu/AppHoldMenu';
import { Img } from '@euler/components/image/AdvancedImage';
import Euler from '@euler/components/layout';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { useAppLoading } from '@euler/components/loading/Provider';
import { adaptiveWrapperOptions } from '@euler/functions/navigationOptionsBuilder';
import { orgMemberToProductAuthor } from '@euler/functions/orgMemberToProductAuthor';
import { useQueryProductDetail } from '@euler/functions/product/useProductDetail';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useLayoutContainerWidth } from '@euler/functions/useAdaptiveContainerWidth';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { OrgMember } from '@euler/model/entity';
import { ProductAuthorType } from '@euler/model/enum';
import {
  ProductAuthorInfo,
  ProductConstructionProcedure,
  ProductWithDetail,
} from '@euler/model/product';
import {
  ConstructionJobProductConfirmationItem,
  ConstructionJobProductProcedureApplyType,
  TaskProductManifestItem,
} from '@euler/model/task-detail';
import { idEquals, safeMarkDevFlag, showAlert } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { MotiView } from 'moti';
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Path, Svg } from 'react-native-svg';
import { useTheme } from 'styled-components';

type Props = {
  jobId: number;
  jobName: string;
  jobManager: ConstructionJobManager;
  updateMode?: boolean;
};

type ProcedureOption = {
  value: ProcedureOptionType;
  text: string;
};

type ProcedureOptionType = ConstructionJobProductProcedureApplyType;

const kSimulateProductDetailError = safeMarkDevFlag(false);

const kProcedureOptions: ProcedureOption[] = [
  { value: 'merge', text: '合并默认工序' },
  { value: 'replace', text: '替换默认工序' },
  { value: 'ignore', text: '不使用该工序' },
];

type State = {
  index: number;
  loading: boolean;
  confirmType?: 'adopt' | 'ignore';
  optionType: ProcedureOptionType;
  qty: number;
  // Closer snapshot at the moment of confirmation. Defaults to the
  // manifest item's persisted closer if set, otherwise to the order's
  // service advisor — a FE-side default so the picker shows a real
  // person instead of "未指定". The BE no longer auto-attributes a
  // closer; whatever the FE sends in the confirm payload is what gets
  // persisted (and if the FE sends nothing, no closer is set).
  closer: ProductAuthorInfo | undefined;
};

function defaultCloserFor(
  manifestItem: TaskProductManifestItem | undefined,
  serviceAgent: { id: number; name: string } | undefined,
): ProductAuthorInfo | undefined {
  if (manifestItem?.closer) return manifestItem.closer;
  if (serviceAgent && serviceAgent.id > 0) {
    return {
      id: serviceAgent.id,
      name: serviceAgent.name,
      type: ProductAuthorType.ServiceAgent,
    };
  }
  return undefined;
}

export const ProductAdoptionConfirmationPage = wrapNavigatorScreen(
  (props: Props) => {
    const isMobile = useIsMobileLayout();
    if (isMobile) {
      return <Content {...props} />;
    }
    return (
      <ModalWrapper dismissible={false}>
        <Content {...props} />
      </ModalWrapper>
    );
  },
  adaptiveWrapperOptions({
    headerShown: false,
  }),
);

const Content = memo(({ jobName, jobManager, updateMode }: Props) => {
  const insets = useSafeAreaInsets();
  const appLoading = useAppLoading();
  const navigation = useTaskNavigation();
  const confirm = useConfirm();

  useToggleAutomaticKeyboard(true);

  const { rpx } = useResponsiveContext();
  const { link: linkColor } = useTheme();

  const [staged] = useBehaviorSubject(jobManager.staged$);
  const manifestItems = useProductManifestItemsForSubject(jobName);

  const done = useRef(false);
  const [productDetail, setProductDetail] = useState<ProductWithDetail>();

  const [state, setState] = useState<State>({
    index: 0,
    loading: false,
    optionType: 'merge',
    confirmType: undefined,
    qty: 1,
    closer: undefined,
  });

  const confirmedProductIdSet = useRef(
    new Set(
      staged.context.productConfirmation?.items.map(x => String(x.productId)) ??
        [],
    ),
  );
  const items = useMemo(
    () =>
      updateMode
        ? manifestItems
        : manifestItems?.filter(
            x => !confirmedProductIdSet.current.has(String(x.product.id)),
          ),
    [manifestItems, updateMode],
  );

  const manifestItem = items?.at(state.index);

  const confirmationItem =
    manifestItem != null
      ? staged.context.productConfirmation?.items.find(
          x =>
            idEquals(x.contractId, manifestItem?.product.sourceContractId) &&
            idEquals(x.productId, manifestItem?.product.id),
        )
      : undefined;

  useEffect(() => {
    if (updateMode) return;
    return navigation.addListener('beforeRemove', e => {
      if (!done.current) {
        e.preventDefault();
      }
    });
  }, [navigation, updateMode]);

  const basicInfo = jobManager.taskManager.cachedDetail?.basicInfo;
  // Memoize the {id,name} pair so a parent re-render doesn't churn the
  // reference and re-trigger the closer-reset effect below.
  const serviceAgent = useMemo(
    () =>
      basicInfo != null
        ? { id: basicInfo.serviceAgentId, name: basicInfo.serviceAgentName }
        : undefined,
    [basicInfo],
  );

  useEffect(() => {
    setState(x => ({
      ...x,
      qty: confirmationItem?.qty ?? 1,
      optionType:
        confirmationItem?.proceduresAppliedAs ??
        productDetail?.detail?.constructionProceduresApplyType ??
        'merge',
    }));
  }, [
    confirmationItem?.proceduresAppliedAs,
    confirmationItem?.qty,
    productDetail?.detail?.constructionProceduresApplyType,
  ]);

  // Reset the picker only when the *target* product changes (different
  // manifest-item id) or its persisted closer changes — never on
  // every render. Without this guard the user's picker selection would
  // be immediately overwritten on the next render.
  const manifestItemId = manifestItem?.id;
  const persistedCloserId = manifestItem?.closer?.id;
  useEffect(() => {
    setState(x => ({
      ...x,
      closer: defaultCloserFor(manifestItem, serviceAgent),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestItemId, persistedCloserId, serviceAgent]);

  const onConfirm = usePersistFn(async (adopted: boolean) => {
    if (manifestItem == null || items == null) return;
    setState(x => ({
      ...x,
      loading: true,
      confirmType: adopted ? 'adopt' : 'ignore',
    }));
    try {
      await jobManager.confirmProductAdoption({
        contractId: manifestItem.product.sourceContractId,
        productId: manifestItem.product.id,
        qty: state.qty,
        adopted,
        // Send the picked closer with the confirm payload. The BE writes
        // it through to the manifest item in the same transaction. The
        // standalone change-closer endpoint is only used when editing the
        // closer of an already-confirmed product.
        closer: adopted ? state.closer : undefined,
        proceduresAppliedAs: adopted ? state.optionType : undefined,
        proceduresRequired:
          productDetail?.detail?.constructionProceduresRequired,
        procedures: productDetail?.detail?.constructionProcedures?.map(x => ({
          name: x.name,
          requiresPhoto: x.requiresPhoto,
          removable: x.removable,
          stdImages: x.stdImages,
        })),
      });
      if (updateMode) {
        setState(x => ({
          ...x,
          loading: false,
          confirmType: undefined,
        }));
        return;
      }
      if (state.index < items.length - 1) {
        setProductDetail(undefined);
        setState(x => ({
          ...x,
          loading: false,
          confirmType: undefined,
          optionType: 'merge',
          index: x.index + 1,
        }));
      } else {
        setState(x => ({ ...x, loading: false, confirmType: undefined }));
        done.current = true;
        navigation.goBack();
        appLoading.show({
          type: 'success',
          message: '已确认',
          duration: 1000,
        });
      }
    } catch (e) {
      void showAlert('出错了', (e as any).message);
      setState(x => ({ ...x, loading: false, confirmType: undefined }));
    }
  });

  const onConfirmAdopt = usePersistFn(async () => {
    const res = await confirm({
      title: '产品确认',
      message: `您正在确认在“${jobName}”施工过程中使用该产品，确认后施工技师将会获得相应的施工奖励，是否继续？`,
      confirmButtonText: '确认',
      cancelButtonText: '取消',
    });
    if (res) {
      void onConfirm(true);
    }
  });

  const onConfirmIgnore = usePersistFn(async () => {
    const res = await confirm({
      title: '产品确认',
      message: `是否确认不使用该产品？`,
      confirmButtonText: '确认',
      cancelButtonText: '取消',
    });
    if (res) {
      void onConfirm(false);
    }
  });

  const onPrev = usePersistFn(() => {
    if (state.index === 0) return;
    setState(x => ({ ...x, index: x.index - 1 }));
  });

  const onNext = usePersistFn(() => {
    if (!confirmationItem || items == null || state.index > items.length - 1) {
      return;
    }
    setState(x => ({ ...x, index: x.index + 1 }));
  });

  const onDone = usePersistFn(() => {
    navigation.goBack();
  });

  const onQtyChange = usePersistFn(async (qty: number) => {
    setState(x => ({ ...x, qty }));
    if (
      updateMode &&
      confirmationItem != null &&
      confirmationItem.adopted &&
      manifestItem != null
    ) {
      try {
        appLoading.show();
        await jobManager.confirmProductAdoption({
          contractId: manifestItem.product.sourceContractId,
          productId: manifestItem.product.id,
          qty,
          adopted: true,
          proceduresAppliedAs: state.optionType,
          proceduresRequired:
            productDetail?.detail?.constructionProceduresRequired,
          procedures: productDetail?.detail?.constructionProcedures?.map(x => ({
            name: x.name,
            requiresPhoto: x.requiresPhoto,
            removable: x.removable,
            stdImages: x.stdImages,
          })),
        });
      } catch (e) {
        void showAlert('出错了', (e as any).message);
      } finally {
        appLoading.hide();
      }
    }
  });

  const onOptionTypeChange = usePersistFn(async (type: ProcedureOptionType) => {
    setState(x => ({ ...x, optionType: type }));
    if (
      updateMode &&
      confirmationItem != null &&
      confirmationItem.adopted &&
      manifestItem != null
    ) {
      try {
        appLoading.show();
        await jobManager.confirmProductAdoption({
          contractId: manifestItem.product.sourceContractId,
          productId: manifestItem.product.id,
          adopted: true,
          proceduresAppliedAs: type,
          qty: state.qty,
          proceduresRequired:
            productDetail?.detail?.constructionProceduresRequired,
          procedures: productDetail?.detail?.constructionProcedures?.map(x => ({
            name: x.name,
            requiresPhoto: x.requiresPhoto,
            removable: x.removable,
            stdImages: x.stdImages,
          })),
        });
      } catch (e) {
        void showAlert('出错了', (e as any).message);
      } finally {
        appLoading.hide();
      }
    }
  });

  const onProductDetailChange = usePersistFn(
    (detail: ProductWithDetail | undefined) => {
      setProductDetail(detail);
    },
  );

  // Two save paths:
  //  · Not-yet-adopted (product hasn't been confirmed in this dialog
  //    yet): the picker just updates client state. The closer ships
  //    with the confirm-adopt request — the BE writes it through to the
  //    manifest item in the same transaction.
  //  · Already-adopted (updateMode re-entry on a confirmed product):
  //    the picker writes through immediately via the change-closer
  //    endpoint, so the user doesn't have to re-confirm to commit a
  //    closer change. A success toast confirms the save. The chip flips
  //    back if the request fails.
  const onCloserPickerPress = usePersistFn(() => {
    const currentItem = manifestItem;
    const adoptedAlready = confirmationItem?.adopted === true;
    navigation.push('_selectCloser', {
      title: '选择成交人',
      onSelect: async (member: OrgMember) => {
        const newCloser = orgMemberToProductAuthor(member);
        const prevCloser = state.closer;
        setState(x => ({ ...x, closer: newCloser }));
        navigation.goBack();
        if (adoptedAlready && currentItem?.id != null) {
          try {
            await jobManager.taskManager.productManifestManager.changeManifestItemCloser(
              currentItem.id,
              newCloser,
            );
            appLoading.show({
              type: 'success',
              message: '已更新',
              duration: 1000,
            });
          } catch (e) {
            console.warn('[adoption] change-closer failed', e);
            setState(x => ({ ...x, closer: prevCloser }));
            void showAlert('更改成交人失败', '请稍后重试');
          }
        }
      },
    });
  });

  return (
    <Euler.View
      flex={1}
      pt={insets.top + rpx(15)}
      pb={Math.max(rpx(15), insets.bottom)}
    >
      <Euler.View paddingHorizontal={rpx(15)} mb={rpx(10)}>
        <Euler.View flexDirection="row" alignItems="center">
          <Euler.View
            width={rpx(8)}
            height={rpx(28)}
            mr={rpx(8)}
            backgroundColor={linkColor}
            top={1}
          />
          <Typography.Title medium size={rpx(28)} lineHeight={rpx(44)}>
            产品确认
          </Typography.Title>
          <StackLayout
            direction="row"
            flex={1}
            justifyContent="flex-end"
            alignItems="center"
            gap={rpx(8)}
          >
            {state.index > 0 && (
              <LinkButton
                onPress={onPrev}
                icon={
                  <Icon
                    name="arrow-thin-left"
                    size={rpx(18)}
                    color={linkColor}
                  />
                }
              />
            )}
            {items != null && items.length > 1 && (
              <Typography.Text size={rpx(28)} lineHeight={rpx(36)}>
                {`${state.index + 1}/${items.length}`}
              </Typography.Text>
            )}
            {items != null &&
              state.index < items.length - 1 &&
              confirmationItem != null && (
                <LinkButton
                  onPress={onNext}
                  icon={
                    <Icon
                      name="arrow-thin-right"
                      size={rpx(18)}
                      color={linkColor}
                    />
                  }
                />
              )}
            {updateMode && (
              <Euler.View pl={rpx(8)}>
                <LinkButton text="完成" onPress={onDone} />
              </Euler.View>
            )}
          </StackLayout>
        </Euler.View>
        <Typography.Body thin mt={rpx(5)} color="#312e2e">
          您正在施工
          <Typography.Text light color="rgba(0, 0, 0, 0.95)">
            {jobManager.jobName}
          </Typography.Text>
          ，继续施工前，请确认是否使用以下产品。当推荐的产品被确认用于施工时，施工技师将会获得相应的施工奖励！
        </Typography.Body>
      </Euler.View>

      {manifestItem != null && (
        <MotiView
          key={manifestItem.id}
          from={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ type: 'timing', duration: 550 }}
          exit={{ opacity: 0 }}
          css={`
            flex: 1;
            padding-left: ${rpx(15)}px;
            padding-right: ${rpx(15)}px;
          `}
        >
          <LayoutProviderView
            css={`
              flex: 1;
            `}
          >
            <ProductCard
              item={manifestItem}
              confirmation={confirmationItem}
              optionType={state.optionType}
              qty={state.qty}
              closer={state.closer}
              onCloserPress={onCloserPickerPress}
              onQtyChange={onQtyChange}
              onOptionTypeChange={onOptionTypeChange}
              onProductDetailChange={onProductDetailChange}
            />
          </LayoutProviderView>
        </MotiView>
      )}
      <Euler.View paddingHorizontal={rpx(15)} pt={rpx(10)}>
        {!confirmationItem?.adopted && (
          <Center.Touchable
            backgroundColor={
              confirmationItem?.adopted ? '#aaa' : StatusColors.Info
            }
            height={rpx(50)}
            borderRadius={rpx(8)}
            disabled={state.loading}
            onPress={onConfirmAdopt}
            flexDirection="row"
          >
            {state.loading && state.confirmType === 'adopt' && (
              <ActivityIndicator
                size="small"
                color="white"
                css={`
                  margin-right: ${rpx(4)}px;
                  transform: scale(0.85);
                `}
              />
            )}
            <Typography.Text color="white" bold size={rpx(18)}>
              确认使用该产品
            </Typography.Text>
          </Center.Touchable>
        )}
        {confirmationItem?.adopted !== false && (
          <Center.Touchable
            height={rpx(44)}
            borderRadius={rpx(10)}
            onPress={onConfirmIgnore}
            flexDirection="row"
            disabled={state.loading}
            backgroundColor={confirmationItem?.adopted ? '#e8461e' : undefined}
          >
            {state.loading && state.confirmType === 'ignore' && (
              <ActivityIndicator
                size="small"
                color="white"
                css={`
                  margin-right: ${rpx(4)}px;
                  transform: scale(0.85);
                `}
              />
            )}
            <Typography.Text
              color={confirmationItem?.adopted ? '#fff' : '#000'}
              family={confirmationItem?.adopted ? 'medium' : 'light'}
              size={confirmationItem?.adopted ? rpx(16) : undefined}
            >
              {confirmationItem?.adopted ? '取消使用该产品' : '暂不使用该产品'}
            </Typography.Text>
          </Center.Touchable>
        )}
      </Euler.View>
    </Euler.View>
  );
});

const ProductCard = memo(
  ({
    item,
    optionType,
    qty,
    closer,
    optionDisabled,
    confirmation,
    onOptionTypeChange,
    onProductDetailChange,
    onQtyChange,
    onCloserPress,
  }: {
    item: TaskProductManifestItem;
    optionType: ProcedureOptionType;
    optionDisabled?: boolean;
    qty: number;
    closer: ProductAuthorInfo | undefined;
    confirmation: ConstructionJobProductConfirmationItem | undefined;
    onOptionTypeChange: (type: ProcedureOptionType) => void;
    onProductDetailChange?: (detail: ProductWithDetail | undefined) => void;
    onQtyChange?: (qty: number) => void;
    onCloserPress?: () => void;
  }) => {
    const { product } = item;
    const detail = useQueryProductDetail(product.id);
    const width = useLayoutContainerWidth();
    const { rpx } = useResponsiveContext();
    const options = kProcedureOptions.filter(
      x =>
        x.value !== 'ignore' ||
        !detail.state?.detail?.constructionProceduresRequired,
    );
    const option = options.find(x => x.value === optionType);
    const navigation = useTaskNavigation();
    const [qtyText, setQtyText] = useState(String(qty));

    const {
      showLeadingMask,
      showTrailingMask,
      onLayout,
      onContentSizeChange,
      onScroll,
    } = useScrollableMask({
      orientation: 'vertical',
    });

    const onViewDetail = usePersistFn(() => {
      navigation.push('_productDetail', {
        id: product.id,
      });
    });

    useEffect(() => {
      onProductDetailChange?.(detail.state);
    }, [detail.state, onProductDetailChange]);

    const onQtyTextChange = usePersistFn(() => {
      const s = qtyText;
      let value = s ? parseInt(s, 10) : qty;
      if (isNaN(value) || value <= 0) value = qty;
      if (value !== qty) {
        onQtyChange?.(value);
      }
      setQtyText(String(value));
    });

    const renderItem: ListRenderItem<ProductConstructionProcedure> =
      useCallback(
        ({ item: procedure, index }) => {
          return (
            <Euler.View
              height={rpx(44)}
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              backgroundColor={'#e2e2e2'}
              paddingHorizontal={rpx(10)}
            >
              <Text
                style={{
                  fontStyle: 'italic',
                  fontWeight: 'bold',
                  fontSize: rpx(21),
                  width: rpx(24),
                }}
              >{`${index + 1}. `}</Text>
              <Typography.Text regular size={rpx(15)} lineHeight={rpx(24)}>
                {procedure.name}
              </Typography.Text>
              {index <
              detail.state!.detail!.constructionProcedures!.length - 1 ? (
                <Euler.View
                  backgroundColor="#bbb"
                  height={StyleSheet.hairlineWidth * 1.2}
                  position="absolute"
                  left={rpx(10)}
                  right={0}
                  bottom={0}
                />
              ) : null}
            </Euler.View>
          );
        },
        [detail.state, rpx],
      );

    return (
      <Euler.View flex={1}>
        <Euler.View width={width} height={width * 0.8} padding={rpx(0)}>
          <Euler.View flex={1} borderRadius={rpx(15)} overflow="hidden">
            <Img
              uri={product.coverUrl}
              style={StyleSheet.absoluteFill}
              resizeMode="cover"
            />
            <BlackOverlay />
            <Euler.View
              style={StyleSheet.absoluteFill}
              justifyContent="space-between"
            >
              <View />
              <BlurView
                blurType="dark"
                style={
                  Platform.OS === 'web'
                    ? {
                        borderBottomLeftRadius: rpx(15),
                        borderBottomRightRadius: rpx(15),
                        overflow: 'hidden',
                      }
                    : undefined
                }
              >
                <Euler.View padding={rpx(10)}>
                  <StackLayout
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={rpx(15)}
                  >
                    <StackLayout
                      direction="row"
                      justifyContent="flex-start"
                      alignItems="center"
                      flex={1}
                    >
                      <Typography.Title regular color="white" size={rpx(18)}>
                        {`${product.brandName}`}
                      </Typography.Title>
                      <Typography.Title
                        regular
                        color="rgba(255, 255, 255, 0.35)"
                        size={rpx(8)}
                        marginHorizontal={rpx(4)}
                      >
                        {`▶`}
                      </Typography.Title>
                      <Typography.Title
                        regular
                        color="white"
                        size={rpx(18)}
                        numberOfLines={1}
                        flex={1}
                        overflow="hidden"
                        ellipsizeMode="middle"
                      >
                        {`${product.name}`}
                      </Typography.Title>
                    </StackLayout>
                    <Center.Touchable onPress={onViewDetail}>
                      <Typography.Text color={StatusColors.Info}>
                        {'查看详情 >'}
                      </Typography.Text>
                    </Center.Touchable>
                  </StackLayout>

                  <Typography.Body
                    color="white"
                    opacity={0.75}
                    size={rpx(14)}
                    thin
                    mt={rpx(5)}
                  >
                    {product.title ?? ''}
                  </Typography.Body>
                </Euler.View>
              </BlurView>
            </Euler.View>
            {confirmation?.adopted && (
              <ConfirmedIcon
                css={`
                  position: absolute;
                  top: ${rpx(5)}px;
                  right: ${rpx(5)}px;
                `}
              />
            )}
          </Euler.View>
        </Euler.View>
        <Euler.View mt={rpx(10)} flex={1}>
          <StackLayout
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={rpx(4)}
            mb={rpx(8)}
            gap={rpx(8)}
            pl={rpx(10)}
          >
            <Typography.Label
              medium
              color="#444"
              size={rpx(16)}
              lineHeight={rpx(22)}
            >
              成交人:
            </Typography.Label>
            <Center.Touchable
              onPress={onCloserPress}
              flexDirection="row"
              alignItems="center"
            >
              <Euler.View
                paddingHorizontal={rpx(14)}
                paddingVertical={rpx(5)}
                borderRadius={rpx(18)}
                backgroundColor="#ffffff"
                borderWidth={rpx(1)}
                borderColor={StatusColors.Info}
              >
                <Typography.Text
                  size={rpx(14)}
                  color={StatusColors.Info}
                  family="medium"
                >
                  {closer?.name ?? '未指定'}
                </Typography.Text>
              </Euler.View>
              <Typography.Text
                size={rpx(13)}
                color={StatusColors.Info}
                style={{ marginLeft: rpx(8) }}
              >
                修改
              </Typography.Text>
            </Center.Touchable>
            <Euler.View flex={1} />
          </StackLayout>
          <StackLayout
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={rpx(4)}
            mb={rpx(10)}
            gap={rpx(4)}
            pl={rpx(10)}
          >
            <Typography.Label
              medium
              color="#444"
              size={rpx(16)}
              lineHeight={rpx(22)}
            >
              使用数量:
            </Typography.Label>
            <TextInput
              keyboardType="numbers-and-punctuation"
              value={qtyText}
              onChangeText={setQtyText}
              onSubmitEditing={onQtyTextChange}
              onBlur={onQtyTextChange}
              returnKeyType="done"
              selectTextOnFocus={true}
              css={`
                width: ${rpx(44)}px;
                height: ${rpx(22)}px;
                font-weight: bold;
                font-size: ${rpx(16)}px;
                line-height: ${rpx(18)}px;
                text-align: center;
                border-color: #ccc;
                border-bottom-width: ${rpx(1)}px;
              `}
            />
            <Typography.Label
              thin
              color="#444"
              flex={1}
              size={rpx(12)}
              lineHeight={rpx(22)}
            >
              (请根据实际施工情况填写该产品实际使用数量)
            </Typography.Label>
          </StackLayout>
          <StackLayout
            direction="row"
            alignItems="center"
            gap={rpx(4)}
            borderBottomColor={'rgba(0, 0, 0, 0.1)'}
            borderBottomWidth={rpx(1)}
            paddingVertical={rpx(8)}
          >
            <Icon name="tools" size={rpx(20)} style={{ top: rpx(1) }} />
            <Typography.Title
              light
              size={rpx(21)}
              lineHeight={rpx(28)}
              flex={1}
            >
              推荐施工工序
            </Typography.Title>
            <Euler.View justifyContent="flex-end" width={rpx(200)}>
              <AppHoldItem
                presentOnPress
                disabled={
                  optionDisabled ||
                  !detail.state?.detail?.constructionProcedures?.length
                }
                placement={{
                  position: 'below',
                  anchor: 'left',
                  align: 'left',
                  matchWidth: true,
                  margin: rpx(5),
                }}
                component={({ close }) => (
                  <AppMenu>
                    {options.map(x => (
                      <AppMenu.Item
                        key={x.value}
                        title={`${x.text}${x.value === optionType ? ' ✓' : ''}`}
                        textStyle={{ textAlign: 'center' }}
                        onPress={() => {
                          close(() => {
                            onOptionTypeChange(x.value);
                          });
                        }}
                      />
                    ))}
                  </AppMenu>
                )}
              >
                <StackLayout
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={rpx(4)}
                >
                  <Typography.Text light size={rpx(14)} color="gray">
                    工序应用方式:
                  </Typography.Text>
                  <Typography.Text
                    color={
                      optionDisabled ||
                      !detail.state?.detail?.constructionProcedures?.length
                        ? 'gray'
                        : StatusColors.Info
                    }
                    size={rpx(14)}
                  >
                    {option?.text ?? '-'}
                  </Typography.Text>
                  <Typography.Text
                    color={
                      optionDisabled ||
                      !detail.state?.detail?.constructionProcedures?.length
                        ? 'gray'
                        : StatusColors.Info
                    }
                  >
                    {'▼'}
                  </Typography.Text>
                </StackLayout>
              </AppHoldItem>
            </Euler.View>
          </StackLayout>
          <Euler.View flex={1}>
            <ListMask
              leadingMask={showLeadingMask}
              trailingMask={showTrailingMask}
              orientation="vertical"
              location={0.25}
              css={`
                flex: 1;
              `}
            >
              <FlatList
                data={detail.state?.detail?.constructionProcedures ?? []}
                keyExtractor={x => x.id}
                renderItem={renderItem}
                showsVerticalScrollIndicator={Platform.OS === 'web'}
                onLayout={onLayout}
                onScroll={onScroll}
                onContentSizeChange={onContentSizeChange}
                contentContainerStyle={{ flexGrow: 1 }}
                ListEmptyComponent={
                  <Center flex={1}>
                    {detail.loading || detail.refreshing ? (
                      <StackLayout
                        direction="row"
                        alignItems="center"
                        gap={rpx(5)}
                      >
                        <ActivityIndicator
                          size="small"
                          style={{ transform: [{ scale: 0.75 }] }}
                        />
                        <Typography.Text color="gray">
                          加载中...{' '}
                        </Typography.Text>
                      </StackLayout>
                    ) : detail.error || kSimulateProductDetailError ? (
                      <>
                        <StackLayout
                          direction="row"
                          alignItems="center"
                          gap={rpx(5)}
                        >
                          <Icon
                            name="error"
                            color={StatusColors.Danger}
                            size={16}
                          />
                          <Typography.Text color={StatusColors.Danger}>
                            加载产品推荐工序失败,
                          </Typography.Text>
                          <LinkButton
                            onPress={() => detail.invalidate()}
                            text="点击重试"
                          />
                        </StackLayout>
                        <Typography.Text
                          align="center"
                          size={rpx(12)}
                          mt={rpx(8)}
                          light
                          color={StatusColors.Danger}
                        >
                          {detail.error?.message ??
                            'An error occurred while loading the data, please try again later. '}
                        </Typography.Text>
                      </>
                    ) : (
                      <StackLayout
                        direction="row"
                        alignItems="center"
                        gap={rpx(5)}
                      >
                        <Icon
                          name="information-solid"
                          size={rpx(16)}
                          color="gray"
                        />
                        <Typography.Text
                          color="gray"
                          size={rpx(15)}
                          lineHeight={rpx(24)}
                        >
                          该产品尚未设置相关推荐工序
                        </Typography.Text>
                      </StackLayout>
                    )}
                  </Center>
                }
              />
            </ListMask>
          </Euler.View>
        </Euler.View>
      </Euler.View>
    );
  },
);

const ConfirmedIcon: FC<IconProps> = memo(props => {
  const { fill = '#63ca58', ...svgProps } = useSvgProps(props, 80, 80);
  return (
    <Svg viewBox="0 0 1024 1024" {...svgProps}>
      <Path
        d="M266.893606 139.438773C61.73811 274.299273 4.653997 549.749766 139.513474 754.904239c134.858454 205.15345 410.307923 262.238586 615.46342 127.380132 205.155497-134.859477 262.238586-410.30997 127.380132-615.465466C747.498572 61.666478 472.047056 4.581342 266.893606 139.438773L266.893606 139.438773M735.033679 851.871711C546.58161 975.513706 293.566082 923.166477 169.924087 734.961024 46.033429 546.507932 98.631368 293.742091 286.834774 169.851433c188.453093-123.890658 441.218934-71.293743 565.109592 116.910687C975.58636 474.965525 923.238109 727.982076 735.033679 851.871711L735.033679 851.871711M735.033679 851.871711"
        fill={fill}
      />
      <Path
        d="M301.292044 191.787024c-175.9882 115.665323-225.096664 352.47782-109.431342 528.46602 115.663276 176.239933 352.476797 225.097688 528.46602 109.432365 176.237887-115.913986 225.096664-352.476797 109.432365-528.46602C714.094788 124.981503 477.531977 76.123748 301.292044 191.787024L301.292044 191.787024M710.605314 814.480102c-167.762865 110.181425-393.357879 63.565734-503.539304-104.19713-110.179378-167.762865-63.563688-393.357879 104.199177-503.538281 167.762865-110.181425 393.357879-63.565734 503.538281 104.19713C924.982846 478.95335 878.368178 704.300724 710.605314 814.480102L710.605314 814.480102M710.605314 814.480102"
        fill={fill}
      />
      <Path
        d="M319.241858 500.142951c3.239787-58.579163 33.403784-114.916263 86.250386-149.814074 52.846602-34.651194 116.661-39.884382 171.751712-19.693528l18.944468-12.464893c-63.316048-27.91784-138.846278-24.179702-201.166649 16.70138-62.068637 40.881083-95.472422 108.685351-94.973048 177.983647L319.241858 500.142951 319.241858 500.142951M319.241858 500.142951"
        fill={fill}
      />
      <Path
        d="M702.628642 521.331529c-3.241834 58.580187-33.403784 114.916263-86.250386 149.816121-52.846602 34.649148-116.661 39.884382-171.751712 19.693528l-18.944468 12.463869c63.316048 27.919886 138.846278 24.179702 201.164602-16.70138 62.070684-40.881083 95.473445-108.684328 94.975095-177.983647L702.628642 521.331529 702.628642 521.331529M702.628642 521.331529"
        fill={fill}
      />
      <Path
        d="M323.728032 225.939869l28.6669 22.932292 30.6603-19.941168-12.96222 34.150798 28.41619 22.932292-36.392862-1.743714-12.963243 34.150798-9.720386-35.147498-36.644595-1.74576 30.662347-19.941168L323.728032 225.939869M323.728032 225.939869"
        fill={fill}
      />
      <Path
        d="M221.025952 331.632049l35.396161 8.723685 19.194154-31.159674 2.742461 36.394908 35.398208 8.475022-33.653471 13.960966 2.742461 36.392862-23.681352-27.91784-33.652448 13.95892 19.194154-31.159674L221.025952 331.632049M221.025952 331.632049"
        fill={fill}
      />
      <Path
        d="M461.57761 173.58957l16.20303 32.905434 36.145222-5.234211-26.174126 25.425066 16.20303 32.654724-32.405037-16.950044-26.176173 25.425066 6.232958-36.144198-32.406061-16.950044 36.146245-5.235235L461.57761 173.58957M461.57761 173.58957"
        fill={fill}
      />
      <Path
        d="M608.900957 184.308703l0.747014 36.394908 35.149545 10.469446-34.651194 12.215206 0.74906 36.394908-22.185278-28.916587-34.649148 11.964496 20.938891-30.16195-22.185278-29.16525 34.898835 10.469446L608.900957 184.308703M608.900957 184.308703"
        fill={fill}
      />
      <Path
        d="M172.416861 470.977701l35.896558-7.227612 4.238534-36.394908 17.946744 31.906687 35.896558-7.227612-24.928762 26.921139 17.948791 31.907711-33.155121-15.20633-24.926716 26.922163 4.236488-36.394908L172.416861 470.977701M172.416861 470.977701"
        fill={fill}
      />
      <Path
        d="M698.142467 795.535635l-28.6669-22.934339-30.6603 19.943215 12.96222-34.150798-28.418236-22.934339 36.394908 1.74576 12.96222-34.150798 9.722432 35.147498 36.643572 1.743714-30.6603 19.943215L698.142467 795.535635M698.142467 795.535635"
        fill={fill}
      />
      <Path
        d="M800.843524 689.843454l-35.398208-8.724709-19.194154 31.159674-2.741437-36.394908-35.398208-8.475022 33.652448-13.95892-2.741437-36.394908 23.681352 27.919886 33.652448-13.95892-19.194154 31.159674L800.843524 689.843454M800.843524 689.843454"
        fill={fill}
      />
      <Path
        d="M560.042179 847.883887l-16.20303-32.905434-36.144198 5.235235 26.174126-25.427112-16.20303-32.654724 32.406061 16.95209 26.174126-25.427112-6.232958 36.144198 32.406061 16.95209-36.146245 5.234211L560.042179 847.883887M560.042179 847.883887"
        fill={fill}
      />
      <Path
        d="M412.969543 837.164754l-0.74906-36.392862-35.147498-10.470469 34.649148-12.213159-0.747014-36.394908 22.185278 28.91454 34.649148-11.964496-20.939915 29.91431 22.185278 29.16525-34.897811-10.469446L412.969543 837.164754M412.969543 837.164754"
        fill={fill}
      />
      <Path
        d="M849.451592 550.496779l-35.894512 7.229658-4.238534 36.394908-17.947767-31.907711-35.896558 7.229658 24.926716-26.922163-17.947767-31.907711 33.153074 15.20633 24.928762-26.921139-4.238534 36.392862L849.451592 550.496779M849.451592 550.496779"
        fill={fill}
      />
      <Path
        d="M424.169629 584.143087c10.122545-1.694595 18.813484-3.532453 26.076912-5.53097 1.075495 2.512217 2.328022 5.712095 3.759627 9.597589 2.852978 7.777127 4.934382 13.922081 6.223748 18.425651 4.524037 12.035104-1.714038 23.521716-18.72548 34.443463l-58.405201 37.497009c-19.279089 12.377912-35.107589 8.927323-47.484478-10.352789L298.482045 610.384751l19.847024-12.741185 8.372691 13.041014 65.776076-42.228777-18.202571-28.351722-89.025593 57.154721-12.013615-18.7132 108.871593-69.895907 42.2298 65.778122-85.6231 54.970986 15.653515 24.381293c5.824659 9.073656 13.077853 10.821463 21.778003 5.236258l50.466392-32.399921c8.834202-6.197142 11.796674-12.378935 8.918114-18.538215-1.322112-2.877537-3.667529-7.779174-7.03523-14.700817C426.445462 589.362972 425.00567 586.283844 424.169629 584.143087z"
        fill={fill}
      />
      <Path
        d="M461.409788 462.541565l9.827833 15.310707-16.443507 10.55745c3.343141 8.538467 5.682419 17.172101 7.02909 25.916253l19.280112-12.377912 45.505403 70.880327-20.980847 13.469779 5.096065 7.937786-14.742772 9.465582-28.031427-43.661406c-0.678452 3.112897-0.853437 6.15314-0.532119 9.148357-6.611581-6.965645-13.367449-12.500709-20.273742-16.61133 0.301875-17.809621-2.809999-35.55682-9.361205-53.245691l-13.042037 8.373715-9.828856-15.310707L461.409788 462.541565zM497.132384 439.607226l33.45495-21.478174 9.465582 14.742772-1.589195 16.234753 18.712177-12.013615 49.5096 77.116355c7.521301 11.712763 6.67298 20.801768-2.526543 27.245528-3.783163 2.428306-7.370875 4.732791-10.773368 6.916527-0.24457-0.38067-0.923022-1.007957-2.0241-1.902326-5.502317-6.076392-9.668197-10.07138-12.503778-11.993149 0.998747-0.103354 2.252297-0.64366 3.767813-1.6158 2.131547-0.830925 3.571339-1.493004 4.332678-1.982144 4.155646-2.667759 5.025457-5.891174 2.596128-9.67536l-5.460362-8.505721-15.309684 9.828856 17.838274 27.78481-15.309684 9.828856-17.839297-27.78481-15.309684 9.828856c7.222495 14.582113 10.92277 27.41949 11.110035 38.510082-7.447623-1.623987-14.532995-2.681062-21.25407-3.170203 0.706081-12.190647-6.04774-28.949332-20.2543-50.256614l-18.566868-28.919656c-0.644683 1.490957-0.989537 3.038196-1.014096 4.656043-6.28924-3.969405-12.782118-7.008624-19.4684-9.120728 6.293333-18.455327 9.051143-37.040614 8.251941-55.746652l19.195178-7.519254C496.488725 433.615769 496.811066 436.612009 497.132384 439.607226zM496.52761 572.121286l6.238075-4.004197-26.939559-41.961694-6.238075 4.004197L496.52761 572.121286zM494.235404 477.502301l24.950252-16.017812 1.223874-16.801664-23.815405 15.289218c0.082888 2.623757-0.423649 6.415107-1.505283 11.376095C494.79106 474.217489 494.505557 476.265124 494.235404 477.502301zM505.60536 490.220974l8.736988 13.608949 14.742772-9.465582-8.736988-13.608949L505.60536 490.220974zM530.724457 529.347087l14.742772-9.465582-8.373715-13.042037-14.742772 9.465582C525.024641 520.469905 527.811104 524.809747 530.724457 529.347087zM559.704488 474.706629l-8.735965-13.608949-15.310707 9.828856 8.736988 13.608949L559.704488 474.706629zM552.403199 497.010611l8.374738 13.042037 15.309684-9.828856-8.373715-13.042037L552.403199 497.010611z"
        fill={fill}
      />
      <Path
        d="M565.499471 398.918525l6.328126-20.078291c4.341888 0.954745 9.927093 2.172479 16.759709 3.654226 5.958712 0.980327 12.796444 2.469238 20.484543 4.465708l-6.936993 22.871917C593.337493 406.946362 581.12638 403.301346 565.499471 398.918525zM647.25959 437.713087c4.66423 3.950985 10.081613 8.218172 16.22759 12.803607-4.949732 19.73139-8.526187 39.105646-10.723226 58.133002l-17.850553-5.353938c1.216711-5.587252 0.25071-10.834766-2.90619-15.752776l-28.758997-44.795229-13.608949 8.736988-11.285021-17.578354 31.754215-20.386306 30.942733 48.198745C643.189901 453.414697 645.255956 445.407326 647.25959 437.713087zM626.626667 344.460216l19.847024-12.741185c5.341658 8.319479 11.513218 18.769482 18.524912 31.347962 29.501918 37.650505 60.154032 55.869449 91.953272 54.67218-0.322341 4.486174-0.565888 10.772344-0.699941 18.865673-0.200568 3.858888-0.370437 6.907317-0.532119 9.149381-26.993794-2.950192-49.544393-13.55983-67.675332-31.822776 7.16212 25.305339 6.854105 50.590212-0.937348 75.873038-8.150634-4.375657-16.387225-7.633864-24.690332-9.771551 7.512091-24.041556 9.039887-43.702338 4.570085-58.987463C663.760402 406.03869 650.297786 380.511293 626.626667 344.460216z"
        fill={fill}
      />
    </Svg>
  );
});
